import { Directive, ElementRef, Input, OnChanges, SimpleChanges, OnDestroy, input } from '@angular/core';
import { Subscription } from 'rxjs';
import { DetectionAnimationService, AnimationState } from '@services/detection-animation-service/detection-animation.service';

@Directive({
  selector: '[appDrawDetections]',
  standalone: false,
})
export class DrawDetectionsDirective implements OnDestroy, OnChanges {
  @Input() appDrawDetections: any;
  @Input() config: { top: number, left: number, ratio: number, offsetX?: number, offsetY?: number };
  readonly filled = input<boolean>(false);
  readonly outlineLineWidth = input<number | null>(null);
  canvas: HTMLCanvasElement;
  canvasContext: CanvasRenderingContext2D;

  scale: number = 1;
  lineDashOffset: number = 0;
  private animationSubscription: Subscription | null = null;
  private isInitialized: boolean = false;
  private strokeColors: { mint300: string, teal350: string } | null = null;
  private static readonly FILL_ALPHA_TARGET = 0.44;
  private static readonly FILL_ANIMATION_DURATION_MS = 400;
  private static readonly OUTLINED_DASH: number[] = [7, 5, 7, 7];
  private static readonly FILLED_DASH: number[] = [7, 3, 7, 5];
  private static readonly OUTLINED_LINE_WIDTH = 2;
  private static readonly FILLED_LINE_WIDTH = 1;
  private static readonly DEFAULT_LINE_WIDTH = 1.5;
  private static readonly DEFAULT_COLOR = 'rgb(237, 4, 0)';
  private fillAlpha: number = 0;
  private fillAnimationFromAlpha: number = 0;
  private fillAnimationTargetAlpha: number = 0;
  private fillAnimationStartMs: number = 0;
  private fillAnimationInitialized: boolean = false;

  constructor(
    elementRef: ElementRef<HTMLCanvasElement>,
    private animationService: DetectionAnimationService
  ) {
    this.canvas = elementRef.nativeElement;
    this.canvasContext = elementRef.nativeElement.getContext('2d');
  }
  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (!this.appDrawDetections || !this.config) {
      this.cleanup();
      return;
    }
    // Ensure canvas dimensions are set
    if (!this.canvas) {
      this.initializeAndDraw();
      return;
    }

    requestAnimationFrame((): void => {
      setTimeout((): void => {
        if (this.canvas.clientWidth > 0 && this.canvas.clientHeight > 0) {
          this.canvas.width = this.canvas.clientWidth;
          this.canvas.height = this.canvas.clientHeight;
          this.initializeAndDraw();
        }
      }, 50);
    });
  }

  private initializeAndDraw(): void {
    if (!this.isInitialized) {
      this.setupAnimationSubscription();
      this.isInitialized = true;
    }
    this.drawDetections();
  }

  private setupAnimationSubscription(): void {
    // Get initial state
    const initialState = this.animationService.getCurrentState();
    this.scale = initialState.scale;
    this.lineDashOffset = initialState.lineDashOffset;

    // Subscribe to animation updates
    this.animationSubscription = this.animationService.getAnimationState().subscribe((state: AnimationState): void => {
      this.scale = state.scale;
      this.lineDashOffset = state.lineDashOffset;
      this.animate();
    });

    // Start the shared animation loop
    this.animationService.startAnimation();
  }

  private drawDetections(): void {
    if (this.appDrawDetections.BoundingBoxes) {
      this.drawCircles(this.appDrawDetections.BoundingBoxes);
    }
    if (this.appDrawDetections.PolygonPointsSequences) {
      this.drawPolygons(this.appDrawDetections.PolygonPointsSequences);
    }
  }

  private animate(): void {
    if (!this.canvas || !this.canvasContext) {
      return;
    }
    this.canvasContext.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.drawDetections();
  }

  private updateFillAlphaAnimation(): void {
    const desiredTarget = this.filled() ? DrawDetectionsDirective.FILL_ALPHA_TARGET : 0;
    if (!this.fillAnimationInitialized) {
      this.fillAlpha = desiredTarget;
      this.fillAnimationTargetAlpha = desiredTarget;
      this.fillAnimationFromAlpha = desiredTarget;
      this.fillAnimationStartMs = performance.now();
      this.fillAnimationInitialized = true;
      return;
    }
    if (desiredTarget !== this.fillAnimationTargetAlpha) {
      this.fillAnimationFromAlpha = this.fillAlpha;
      this.fillAnimationTargetAlpha = desiredTarget;
      this.fillAnimationStartMs = performance.now();
    }
    const elapsed = performance.now() - this.fillAnimationStartMs;
    const progress = Math.min(1, elapsed / DrawDetectionsDirective.FILL_ANIMATION_DURATION_MS);
    const eased = progress * (2 - progress);
    this.fillAlpha = this.fillAnimationFromAlpha
      + (this.fillAnimationTargetAlpha - this.fillAnimationFromAlpha) * eased;
  }

  private cleanup(): void {
    if (this.animationSubscription) {
      this.animationSubscription.unsubscribe();
      this.animationSubscription = null;
    }
    this.animationService.stopAnimation();
    this.isInitialized = false;
  }

  ngOnDestroy(): void {
    this.cleanup();
  }

  drawCircles(circles: string[]): void {
    circles.forEach((pointString: string, index: number): void => {
      const point: number[] = pointString.split(',').map((x: string): number => +x);

      const centerX = (((point[0] + point[2]) / 2 - this.config.left) * this.config.ratio) + (this.config.offsetX || 0);
      const centerY = (((point[1] + point[3]) / 2 - this.config.top) * this.config.ratio) + (this.config.offsetY || 0);
      const radius = (Math.abs(point[0] - point[2]) * this.config.ratio) / 2;
      const scaledX = centerX - (centerX * this.scale);
      const scaledY = centerY - (centerY * this.scale);
      this.canvasContext.beginPath();
      this.canvasContext.setTransform(this.scale, 0, 0, this.scale, scaledX, scaledY);
      this.canvasContext.arc(centerX, centerY, radius, 0, 2 * Math.PI);
      this.canvasContext.setTransform(1, 0, 0, 1, 0, 0);
      this.canvasContext.lineWidth = 2;
      this.canvasContext.strokeStyle = 'rgba(237, 4, 0)';
      this.canvasContext.stroke();
      this.canvasContext.closePath();
    });
  }

  drawPolygons(polygons: (string[] | { points: string[], color: string })[]): void {
    this.updateFillAlphaAnimation();
    polygons.forEach((polygon: (string[] | { points: string[], color: string })): void => {
      if ('color' in polygon) {
        this.renderCustomColorPolygon(polygon);
        return;
      }
      if (this.isScalpContext()) {
        this.renderScalpPolygon(polygon);
        return;
      }
      this.renderDefaultPolygon(polygon);
    });
  }

  private isScalpContext(): boolean {
    return this.filled()
      || this.outlineLineWidth() != null
      || this.fillAlpha > 0.001;
  }

  private renderCustomColorPolygon(polygon: { points: string[], color: string }): void {
    const dash = this.outlineLineWidth() == null
      ? DrawDetectionsDirective.FILLED_DASH
      : DrawDetectionsDirective.OUTLINED_DASH;
    this.tracePolygonPath(polygon, dash);
    if (this.fillAlpha > 0.001) this.fillPolygon(polygon.color);
    const width = this.outlineLineWidth()
      ?? (this.filled()
        ? DrawDetectionsDirective.FILLED_LINE_WIDTH
        : DrawDetectionsDirective.DEFAULT_LINE_WIDTH);
    this.strokePolygon(polygon.color, width);
    this.canvasContext.closePath();
  }

  private renderDefaultPolygon(polygon: string[]): void {
    this.tracePolygonPath(polygon, DrawDetectionsDirective.FILLED_DASH);
    this.strokePolygon(
      DrawDetectionsDirective.DEFAULT_COLOR,
      DrawDetectionsDirective.DEFAULT_LINE_WIDTH,
    );
    this.canvasContext.closePath();
  }

  private renderScalpPolygon(polygon: string[]): void {
    const colors = this.getStrokeColors();
    const progress = Math.min(
      1,
      this.fillAlpha / DrawDetectionsDirective.FILL_ALPHA_TARGET,
    );
    const outlinedAlpha = 1 - progress;
    const filledAlpha = progress;

    if (this.fillAlpha > 0.001) {
      this.tracePolygonPath(polygon, DrawDetectionsDirective.FILLED_DASH);
      this.fillPolygon(colors.teal350);
      this.canvasContext.closePath();
    }

    if (outlinedAlpha > 0.001) {
      this.tracePolygonPath(polygon, DrawDetectionsDirective.OUTLINED_DASH);
      this.strokePolygon(
        this.applyAlpha(colors.mint300, outlinedAlpha),
        DrawDetectionsDirective.OUTLINED_LINE_WIDTH,
      );
      this.canvasContext.closePath();
    }

    if (filledAlpha > 0.001) {
      this.tracePolygonPath(polygon, DrawDetectionsDirective.FILLED_DASH);
      this.strokePolygon(
        this.applyAlpha(colors.teal350, filledAlpha),
        DrawDetectionsDirective.FILLED_LINE_WIDTH,
      );
      this.canvasContext.closePath();
    }
  }

  private applyAlpha(color: string, alpha: number): string {
    if (alpha >= 0.999) return color;
    const hex = color.trim();
    if (!hex.startsWith('#')) return color;
    const body = hex.slice(1);
    const full = body.length === 3
      ? body.split('').map((c: string): string => c + c).join('')
      : body;
    const r = Number.parseInt(full.substring(0, 2), 16);
    const g = Number.parseInt(full.substring(2, 4), 16);
    const b = Number.parseInt(full.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  private tracePolygonPath(
    polygon: string[] | { points: string[], color: string },
    dashPattern: number[],
  ): void {
    this.canvasContext.beginPath();
    this.canvasContext.setLineDash(dashPattern);
    this.canvasContext.lineDashOffset = this.lineDashOffset;
    const points = ('points' in polygon) ? polygon.points : polygon;
    points.forEach((pointString: string | number[], index: number): void => {
      const [x, y] = this.toCanvasCoords(pointString);
      if (index === 0) this.canvasContext.moveTo(x, y);
      else this.canvasContext.lineTo(x, y);
    });
  }

  private toCanvasCoords(pointString: string | number[]): [number, number] {
    const point = typeof pointString === 'string'
      ? pointString.split(',').map((x: string): number => +x)
      : pointString;
    const x = ((point[0] - this.config.left) * this.config.ratio) + (this.config.offsetX || 0);
    const y = ((point[1] - this.config.top) * this.config.ratio) + (this.config.offsetY || 0);
    return [x, y];
  }

  private getStrokeColors(): { mint300: string, teal350: string } {
    if (!this.strokeColors) {
      const rootStyle = getComputedStyle(document.documentElement);
      this.strokeColors = {
        mint300: rootStyle.getPropertyValue('--cureskin-mint-300').trim(),
        teal350: rootStyle.getPropertyValue('--cureskin-teal-350').trim(),
      };
    }
    return this.strokeColors;
  }

  private fillPolygon(color: string): void {
    this.canvasContext.save();
    this.canvasContext.globalAlpha = this.fillAlpha;
    this.canvasContext.fillStyle = color;
    this.canvasContext.fill();
    this.canvasContext.restore();
  }

  private strokePolygon(color: string, width: number): void {
    this.canvasContext.lineWidth = width;
    this.canvasContext.lineJoin = 'round';
    this.canvasContext.lineCap = 'round';
    this.canvasContext.strokeStyle = color;
    this.canvasContext.stroke();
  }
}
